import React, { useState, useEffect } from 'react';
import { getPersonalBest, PersonalBest as PersonalBestType, getAllUsers, UserStatistics, getTournamentUserRanking } from '../../services/statisticsService';
import { formatDateFromArray, formatDistance, formatTime } from '../../utils/formatters';
import { 
  FaTrophy, 
  FaRoad, 
  FaClock, 
  FaTachometerAlt, 
  FaStopwatch, 
  FaMountain, 
  FaSpinner, 
  FaExclamationTriangle,
  FaUser,
  FaMedal,
  FaCrown,
  FaRunning
} from 'react-icons/fa';
import Select from 'react-select';
import { useTheme } from '../../context/ThemeContext';

interface PersonalAchievementsProps {
  startDateLocal: string;
  endDate: string;
}

// Interface mở rộng để xử lý cả trường hợp API trả về userId hoặc id
interface ExtendedUserStatistics extends UserStatistics {
  id?: number;
}

// Interface cho options của Select component
interface UserOption {
  value: number;
  label: string;
  data: ExtendedUserStatistics;
}

const PersonalAchievements: React.FC<PersonalAchievementsProps> = ({ startDateLocal, endDate }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState<boolean>(false);
  const [usersLoading, setUsersLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [personalBest, setPersonalBest] = useState<PersonalBestType | null>(null);
  const [users, setUsers] = useState<ExtendedUserStatistics[]>([]);
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedOption, setSelectedOption] = useState<UserOption | null>(null);
  const [topUsers, setTopUsers] = useState<UserStatistics[]>([]);
  const [topUsersLoading, setTopUsersLoading] = useState<boolean>(true);

  // Lấy danh sách người dùng
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setUsersLoading(true);
        console.log("Đang gọi API lấy danh sách người dùng...");
        const data = await getAllUsers();
        console.log("Danh sách người dùng nhận được:", data);
        
        // Đảm bảo rằng danh sách người dùng không rỗng và có đầy đủ thông tin
        const userData = data.filter(user => 
          user && user.fullName && user.fullName.trim() !== ''
        ) as ExtendedUserStatistics[];
        
        setUsers(userData);
        
        // Tạo options cho Select component
        const options = userData.map(user => ({
          value: getUserId(user),
          label: user.fullName,
          data: user
        }));
        
        console.log("Options được tạo cho dropdown:", options);
        setUserOptions(options);
      } catch (err: any) {
        console.error('Lỗi chi tiết khi lấy danh sách người dùng:', err);
        setError(err.message || 'Có lỗi xảy ra khi lấy danh sách người dùng');
      } finally {
        setUsersLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Lấy top người dùng từ giải đấu mới nhất (giả định là giải đấu đầu tiên)
  useEffect(() => {
    const fetchTopUsers = async () => {
      try {
        setTopUsersLoading(true);
        
        // Giả định đây là ID của giải đấu mới nhất (điều chỉnh nếu cần)
        const tournamentId = 5;
        const data = await getTournamentUserRanking(tournamentId, startDateLocal, endDate);
        
        // Sắp xếp theo quãng đường và lấy top 3
        const sortedUsers = [...data].sort((a, b) => b.totalDistance - a.totalDistance);
        setTopUsers(sortedUsers.slice(0, 3));
      } catch (err: any) {
        console.error('Lỗi khi lấy top người dùng:', err);
        // Không hiện lỗi này cho người dùng, chỉ ghi log
      } finally {
        setTopUsersLoading(false);
      }
    };

    fetchTopUsers();
  }, [startDateLocal, endDate]);

  // Lấy thành tích cá nhân khi chọn người dùng hoặc thay đổi khoảng thời gian
  useEffect(() => {
    const fetchPersonalBest = async () => {
      if (!selectedUserId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        console.log("Đang gọi API thành tích cá nhân với:", {
          userId: selectedUserId,
          startDateLocal,
          endDate
        });
        
        const data = await getPersonalBest(selectedUserId, startDateLocal, endDate);
        console.log("Dữ liệu thành tích cá nhân nhận được:", data);
        setPersonalBest(data);
      } catch (err: any) {
        console.error('Lỗi chi tiết khi lấy thành tích cá nhân:', err);
        setError(err.message || 'Có lỗi xảy ra khi lấy thành tích cá nhân');
      } finally {
        setLoading(false);
      }
    };

    if (selectedUserId) {
      fetchPersonalBest();
    }
  }, [selectedUserId, startDateLocal, endDate]);

  // Helper function để lấy ID người dùng từ đối tượng người dùng
  const getUserId = (user: ExtendedUserStatistics): number => {
    return user.userId || user.id || 0;
  };

  // Xử lý thay đổi người dùng được chọn
  const handleUserChange = (option: UserOption | null) => {
    setSelectedOption(option);
    if (option) {
      const newUserId = option.value;
      console.log("Đã chọn người dùng mới:", newUserId);
      setSelectedUserId(newUserId);
    } else {
      setSelectedUserId(null);
    }
  };

  // Custom styles cho react-select
  const customStyles = {
    container: (provided: any) => ({
      ...provided,
      width: '100%',
      fontSize: '14px',
      color: '#333',
    }),
    control: (provided: any, state: any) => ({
      ...provided,
      background: '#fff',
      borderColor: state.isFocused ? '#20757A' : '#ccc',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderRadius: '4px',
      boxShadow: state.isFocused ? '0 0 0 1px #20757A' : 'none',
      minHeight: '38px',
      color: '#333',
      '&:hover': {
        borderColor: state.isFocused ? '#20757A' : '#aaa'
      }
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#20757A' : state.isFocused ? '#e6f7f8' : 'white',
      color: state.isSelected ? 'white' : '#333',
      cursor: 'pointer',
      padding: '8px 12px',
      fontWeight: '400',
      fontSize: '14px',
      '&:active': {
        backgroundColor: '#20757A'
      }
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: '#333',
      fontWeight: '400',
      fontSize: '14px',
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 10,
      color: '#333',
      backgroundColor: 'white',
      border: '1px solid #ccc',
      borderRadius: '4px',
      marginTop: '4px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: '#999',
      fontWeight: '400',
      fontSize: '14px',
    }),
    input: (provided: any) => ({
      ...provided,
      color: '#333',
      fontWeight: '400',
      fontSize: '14px',
    }),
    menuList: (provided: any) => ({
      ...provided,
      color: '#333',
      backgroundColor: 'white',
      padding: '4px',
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: '2px 8px',
      color: '#333',
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: '#666',
      padding: '8px',
      '&:hover': {
        color: '#20757A'
      }
    }),
    clearIndicator: (provided: any) => ({
      ...provided,
      color: '#666',
      padding: '8px',
      '&:hover': {
        color: '#20757A'
      }
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      color: '#666',
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      backgroundColor: '#ccc',
      margin: '4px 0',
    }),
    group: (provided: any) => ({
      ...provided,
      color: '#333',
    }),
    groupHeading: (provided: any) => ({
      ...provided,
      color: '#333',
      fontSize: '14px',
      fontWeight: 'bold',
      padding: '8px 12px',
      backgroundColor: '#f4f4f4',
    }),
    loadingMessage: (provided: any) => ({
      ...provided,
      color: '#333',
      padding: '8px 12px',
    }),
    noOptionsMessage: (provided: any) => ({
      ...provided,
      color: '#333',
      padding: '8px 12px',
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: '#e6f7f8',
      borderRadius: '2px',
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: '#333',
      padding: '4px',
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: '#666',
      '&:hover': {
        backgroundColor: '#20757A',
        color: 'white'
      }
    })
  };

  const selectProps = {
    id: "user-select",
    options: userOptions,
    value: selectedOption,
    onChange: handleUserChange,
    placeholder: "-- Tìm và chọn người dùng --",
    isClearable: true,
    isSearchable: true,
    styles: customStyles,
    className: "react-select-container",
    classNamePrefix: "react-select",
    menuPortalTarget: document.body,
    theme: (theme: any) => ({
      ...theme,
      colors: {
        ...theme.colors,
        neutral0: 'white',  // background color
        neutral10: '#f4f4f4', // selected item background
        neutral20: '#cccccc', // border color
        neutral30: '#b3b3b3', // border color (focused)
        neutral40: '#999999', // icons color
        neutral50: '#808080', // placeholder color
        neutral60: '#666666', // text color
        neutral70: '#4d4d4d', // label color
        neutral80: '#333333', // text color
        neutral90: '#1a1a1a', // selected text color
        primary: '#20757A', // primary color
        primary25: '#e6f7f8', // hover background
        primary50: '#a3dde0', // active background
        primary75: '#5cbec1', // focus border
        danger: '#C1322D', // danger color
        dangerLight: '#ffebee' // danger background
      }
    })
  };

  // Hiển thị top 3 người dùng trong dạng bục xếp hạng
  const renderTopUsersPodium = () => {
    if (topUsersLoading) {
      return (
        <div className="loading-state" style={{ minHeight: '200px' }}>
          <FaSpinner className="icon-spin" size={30} color="#20757A" />
        </div>
      );
    }

    if (topUsers.length === 0) {
      return (
        <div className="empty-state" style={{ padding: '20px' }}>
          <div className="empty-state-text">Không có dữ liệu xếp hạng</div>
        </div>
      );
    }

    return (
      <div className="top3-users">
        {topUsers.map((user, index) => (
          <div key={user.userId} className={`top-user-podium top-${index + 1}`}>
            <div className="medal-icon">
              {index === 0 && <FaMedal color="#FFD700" size={24} />}
              {index === 1 && <FaMedal color="#C0C0C0" size={22} />}
              {index === 2 && <FaMedal color="#CD7F32" size={20} />}
            </div>
            
            {user.avatarUrl ? (
              <img 
                src={user.avatarUrl} 
                alt={user.fullName}
                className="top-user-avatar"
              />
            ) : (
              <div className="top-user-avatar-placeholder">
                {user.fullName.charAt(0).toUpperCase()}
              </div>
            )}
            
            <div className="top-user-name">{user.fullName}</div>
            <div className="top-user-distance">
              <FaRunning size={12} style={{ marginRight: 5 }} /> 
              {formatDistance(user.totalDistance)}
            </div>
            <div className="top-user-activities">
              <FaStopwatch size={12} style={{ marginRight: 5 }} />
              {user.activityCount} hoạt động
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (usersLoading) {
    return (
      <div className="loading-state">
        <FaSpinner className="icon-spin" size={40} color="#20757A" />
        <p>Đang tải danh sách người dùng...</p>
      </div>
    );
  }

  // Hiển thị màn hình chọn người dùng nếu chưa chọn
  if (!selectedUserId) {
    return (
      <div>
        <div className="statistics-filters">
          <div className="filter-item">
            <label htmlFor="user-select">Vui lòng chọn người dùng</label>
            <div style={{ 
              color: '#333',
              fontSize: '14px',
              fontWeight: 'normal'
            }}>
              <Select {...selectProps} />
            </div>
          </div>
        </div>
        
        {/* Hiển thị bục top 3 người dùng */}
        <div className={`statistics-card ${theme}`}>
          <div className="statistics-card-header">
            <h2 className="statistics-card-title">
              <FaCrown /> Top cá nhân xuất sắc
            </h2>
          </div>
          
          {renderTopUsersPodium()}
        </div>
        
        <div className="empty-state">
          <div className="empty-state-icon">
            <FaUser />
          </div>
          <div className="empty-state-text">Vui lòng chọn người dùng để xem thành tích cá nhân chi tiết</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-state">
        <FaSpinner className="icon-spin" size={40} color="#20757A" />
        <p>Đang tải dữ liệu thành tích cá nhân...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="statistics-filters">
          <div className="filter-item">
            <label htmlFor="user-select">Người dùng</label>
            <div style={{ 
              color: '#333',
              fontSize: '14px',
              fontWeight: 'normal'
            }}>
              <Select {...selectProps} />
            </div>
          </div>
        </div>
        
        <div className="error-message">
          <FaExclamationTriangle size={20} />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!personalBest) {
    return (
      <div>
        <div className="statistics-filters">
          <div className="filter-item">
            <label htmlFor="user-select">Người dùng</label>
            <div style={{ 
              color: '#333',
              fontSize: '14px',
              fontWeight: 'normal'
            }}>
              <Select {...selectProps} />
            </div>
          </div>
        </div>
        
        <div className="empty-state">
          <div className="empty-state-icon">
            <FaTrophy />
          </div>
          <div className="empty-state-text">Không có dữ liệu thành tích cá nhân</div>
        </div>
      </div>
    );
  }

  const achievements = [
    {
      id: 'longestDistance',
      title: 'Quãng đường dài nhất',
      icon: <FaRoad />,
      data: personalBest.longestDistance || {
        activityId: 0,
        activityName: 'Không có dữ liệu',
        activityType: '-',
        value: 0,
        valueFormatted: 'Không có dữ liệu',
        activityDate: [0, 0, 0, 0, 0]
      },
      valueDisplay: personalBest.longestDistance?.valueFormatted || 'Không có dữ liệu'
    },
    {
      id: 'longestMovingTime',
      title: 'Thời gian di chuyển dài nhất',
      icon: <FaClock />,
      data: personalBest.longestMovingTime || {
        activityId: 0,
        activityName: 'Không có dữ liệu',
        activityType: '-',
        value: 0,
        valueFormatted: 'Không có dữ liệu',
        activityDate: [0, 0, 0, 0, 0]
      },
      valueDisplay: personalBest.longestMovingTime?.valueFormatted || 'Không có dữ liệu'
    },
    {
      id: 'fastestSpeed',
      title: 'Tốc độ nhanh nhất',
      icon: <FaTachometerAlt />,
      data: personalBest.fastestSpeed || {
        activityId: 0,
        activityName: 'Không có dữ liệu',
        activityType: '-',
        value: 0,
        valueFormatted: 'Không có dữ liệu',
        activityDate: [0, 0, 0, 0, 0]
      },
      valueDisplay: personalBest.fastestSpeed?.valueFormatted || 'Không có dữ liệu'
    },
    {
      id: 'bestPace',
      title: 'Pace tốt nhất',
      icon: <FaStopwatch />,
      data: personalBest.bestPace || {
        activityId: 0,
        activityName: 'Không có dữ liệu',
        activityType: '-',
        value: 0,
        valueFormatted: 'Không có dữ liệu',
        activityDate: [0, 0, 0, 0, 0]
      },
      valueDisplay: personalBest.bestPace?.valueFormatted || 'Không có dữ liệu'
    },
    {
      id: 'highestElevationGain',
      title: 'Độ cao leo nhiều nhất',
      icon: <FaMountain />,
      data: personalBest.highestElevationGain || {
        activityId: 0,
        activityName: 'Không có dữ liệu',
        activityType: '-',
        value: 0,
        valueFormatted: 'Không có dữ liệu',
        activityDate: [0, 0, 0, 0, 0]
      },
      valueDisplay: personalBest.highestElevationGain?.valueFormatted || 'Không có dữ liệu'
    }
  ];

  return (
    <div>
      <div className="statistics-filters">
        <div className="filter-item">
          <label htmlFor="user-select">Người dùng</label>
          <div style={{ 
            color: '#333',
            fontSize: '14px',
            fontWeight: 'normal'
          }}>
            <Select {...selectProps} />
          </div>
        </div>
      </div>
      
      <div className={`statistics-card ${theme}`}>
        <div className="statistics-card-header">
          <h2 className="statistics-card-title">
            <FaTrophy /> Thành tích cá nhân
          </h2>
        </div>
        
        <div className="personal-achievements-container">
          <div className="personal-achievements-user">
            <div className="user-profile">
              {personalBest.avatarUrl ? (
                <img 
                  src={personalBest.avatarUrl} 
                  alt={personalBest.fullName} 
                  className="user-profile-avatar"
                />
              ) : (
                <div className="user-profile-avatar-placeholder">
                  {personalBest.fullName.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="user-profile-info">
                <h3 className="user-profile-name">{personalBest.fullName}</h3>
                <div className="user-profile-subtitle">Thành tích cá nhân tốt nhất</div>
              </div>
            </div>
          </div>
          
          <div className="achievements-grid">
            {achievements.map((achievement) => (
              <div className="achievement-tile" key={achievement.id}>
                <div className="achievement-tile-header">
                  <div className="achievement-tile-icon">
                    {achievement.icon}
                  </div>
                  <div className="achievement-tile-title">{achievement.title}</div>
                </div>
                
                <div className="achievement-tile-value">{achievement.valueDisplay}</div>
                
                {achievement.data && achievement.data.activityId > 0 ? (
                  <div className="achievement-tile-details">
                    <div className="achievement-tile-activity">
                      {achievement.data.activityName}
                    </div>
                    <div className="achievement-tile-meta">
                      <span className="achievement-tile-type">{achievement.data.activityType}</span>
                      <span className="achievement-tile-date">{formatDateFromArray(achievement.data.activityDate)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="achievement-tile-empty">
                    <span>Chưa có hoạt động</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalAchievements; 